import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetPayableFeesQueryFilters,
  InvoicesReportingDayPastDue,
  PayableFeeStatus,
  PaymentFrequencyType,
  ProductTypeId,
  SchoolYear,
} from '@schooly/api';
import {
  getSchoolYearForDateRange,
  getSelectedItemsWithGrouping,
  SelectedItem,
  SelectedItemWithGrouping,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type UsePayableFeesFiltersProps = {
  schoolId: string;
  filters: GetPayableFeesQueryFilters;
  onSetFilters: (filters: GetPayableFeesQueryFilters) => void;
  schoolYears: SchoolYear[];
};

export const usePayableFeesFilters = ({
  schoolId,
  filters,
  onSetFilters,
  schoolYears,
}: UsePayableFeesFiltersProps) => {
  const ageGroups = useAgeGroups({
    schoolId: schoolId,
    userType: SchoolUserRole.Student,
  });
  const { getAgeGroupById, schoolLevelsWithAgeGroupsMap } = ageGroups;
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetFiltersForKey = (key: keyof typeof filters) => (v: string[]) => {
      onSetFilters({
        ...filters,
        [key]: v,
      });
    };

    const onSetProductAndProductType = (productIds: string[], productTypeIds: ProductTypeId[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.ProductType]: productTypeIds,
        [FilterKeys.Product]: productIds,
      });
    };

    const onSetFrequency = (v: PaymentFrequencyType[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Frequency]: v,
      });
    };

    const onSetFeeStatus = (v: PayableFeeStatus[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.FeeStatus]: v,
      });
    };

    const onSetDayPastDue = (v: InvoicesReportingDayPastDue[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.DayPastDue]: v,
      });
    };

    const onSelectProduct = (productId: string) => {
      const updatedProductTypes = (filters.product_type_ids || []).filter(
        (id) => id.productId !== productId,
      );

      const updatedProducts = filters.product_ids?.includes(productId)
        ? filters.product_ids?.filter((ct) => ct !== productId)
        : [...(filters.product_ids || []), productId];

      onSetProductAndProductType(updatedProducts, updatedProductTypes);
    };

    const onSelectProductType = (v: ProductTypeId) => {
      const updatedProducts = (filters.product_ids || []).filter((id) => id !== v.productId);

      const updatedProductTypes = filters.product_type_ids?.some((p) => p.isEqual(v))
        ? filters.product_type_ids?.filter((p) => !p.isEqual(v))
        : [...(filters.product_type_ids || []), v];

      onSetProductAndProductType(updatedProducts, updatedProductTypes);
    };

    return {
      onSetDate: ([fromDate, toDate]: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(fromDate, DEFAULT_DATE_FORMAT_FNS),
            format(toDate, DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onClearProduct: () => {
        onSetFilters({
          ...filters,
          [FilterKeys.Product]: [],
          [FilterKeys.ProductType]: [],
        });
      },
      onSelectProduct,
      onSelectProductType,
      onSetProductAndProductType,
      onSetFrequency,
      onClearFrequency: clearFilter(FilterKeys.Frequency),
      onSetFeeStatus,
      onClearFeeStatus: clearFilter(FilterKeys.FeeStatus),
      onSetDayPastDue,
      onClearDayPastDue: clearFilter(FilterKeys.DayPastDue),
      onSetAgeGroup: onSetFiltersForKey(FilterKeys.InvoicesAgeGroup),
      onClearAgeGroup: clearFilter(FilterKeys.InvoicesAgeGroup),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersProduct = filters.product_ids;
  const filtersProductTypes = filters.product_type_ids;
  const filtersFrequencies = filters.frequency_types;
  const filtersFeeStatus = filters.fee_status;
  const filtersDayPastDue = filters.day_past_due;
  const filtersAgeGroup = filters.age_group_ids;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Period' }),
    [FilterKeys.Product]: formatMessage({ id: 'products-Product' }),
    [FilterKeys.ProductType]: formatMessage({ id: 'products-Product' }),
    [FilterKeys.Frequency]: formatMessage({ id: 'frequencies-Frequency' }),
    [FilterKeys.FeeStatus]: formatMessage({ id: 'payableFees-status' }),
    [FilterKeys.DayPastDue]: formatMessage({ id: 'payableFees-daysPastDue' }),
    [FilterKeys.InvoicesAgeGroup]: formatMessage({ id: 'schoolProperty-AgeGroup' }),
    arrangeBy: formatMessage({ id: 'filter-ArrangeBy' }),
  };

  const selectedItemsForAgeGroups: SelectedItemWithGrouping[] = useMemo(() => {
    const selectedAgeGroups =
      filtersAgeGroup?.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);

        return ageGroup
          ? [
              ...acc,
              {
                id: ageGroup.id,
                groupId: ageGroup.level_id,
              },
            ]
          : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [filtersAgeGroup, schoolLevelsWithAgeGroupsMap, getAgeGroupById]);

  const schoolYear = useMemo(
    () =>
      filters.date
        ? getSchoolYearForDateRange({
            start: newDateTimezoneOffset(filters.date[0]),
            end: newDateTimezoneOffset(filters.date[1]),
            schoolYears,
          })
        : null,
    [filters.date, schoolYears],
  );

  return {
    ...ageGroups,
    ...actions,
    labels,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersProduct,
    filtersProductTypes,
    filtersFrequencies,
    filtersFeeStatus,
    filtersDayPastDue,
    filtersAgeGroup,
    schoolYear,
  };
};
